import {
  type ColumnDef,
  type ColumnSizingState,
  functionalUpdate,
  type OnChangeFn,
  type RowData,
  type TableOptions,
  useTable,
} from "@tanstack/react-table";
import { useCallback, useEffect, useState } from "react";

import { type DashboardTable, dashboardTableFeatures, resizableTableProps } from "./helpers";

export type PersistentResizableTableOptions<TData extends RowData> = Omit<
  TableOptions<typeof dashboardTableFeatures, TData>,
  "columns" | "features"
> & {
  columns: ReadonlyArray<ColumnDef<typeof dashboardTableFeatures, TData, any>>;
};

export type TableSizingPersistence = {
  key: string;
  read: (key: string) => ColumnSizingState;
  write: (key: string, value: ColumnSizingState) => void;
};

type KeyedColumnSizing = {
  key: string;
  value: ColumnSizingState;
};

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = window.setTimeout(() => setDebouncedValue(value), delay);

    return () => window.clearTimeout(handler);
  }, [delay, value]);

  return debouncedValue;
}

/**
 * Generic resizable table state. Product-specific identity, environment, and storage
 * concerns enter through the typed persistence boundary.
 */
export function usePersistentResizableTable<TData extends RowData>(
  options: PersistentResizableTableOptions<TData>,
  persistence: TableSizingPersistence,
): DashboardTable<TData> {
  const { key: storageKey, read, write } = persistence;
  const [columnSizing, setColumnSizing] = useState<KeyedColumnSizing>(() => ({
    key: storageKey,
    value: read(storageKey),
  }));
  const debouncedColumnSizing = useDebouncedValue(columnSizing, 300);

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setColumnSizing({ key: storageKey, value: read(storageKey) });
  }, [read, storageKey]);

  useEffect(() => {
    if (debouncedColumnSizing.key === storageKey) {
      write(storageKey, debouncedColumnSizing.value);
    }
  }, [debouncedColumnSizing, storageKey, write]);

  const handleColumnSizingChange: OnChangeFn<ColumnSizingState> = useCallback(
    (updater) => {
      setColumnSizing((previous) => ({
        key: storageKey,
        value: functionalUpdate(updater, previous.key === storageKey ? previous.value : read(storageKey)),
      }));
    },
    [read, storageKey],
  );
  const [tableData, setTableData] = useState(options.data);

  // TanStack Table requires data to have a stable reference. Preserve it when a caller recreates an array with the same rows.
  if (tableData.length !== options.data.length || tableData.some((row, index) => row !== options.data[index])) {
    setTableData(options.data);
  }

  const columns = options.columns as ReadonlyArray<ColumnDef<typeof dashboardTableFeatures, TData, unknown>>;

  return useTable({
    features: dashboardTableFeatures,
    ...options,
    columns,
    data: tableData,
    ...resizableTableProps,
    state: {
      ...options.state,
      columnSizing: columnSizing.value,
    },
    onColumnSizingChange: handleColumnSizingChange,
  });
}
